import { useState, useEffect } from 'react';
import { Plus, LayoutTemplate, Search, Image, ExternalLink, Loader2, Save, Copy, Trash2, FileText, Info, Braces } from 'lucide-react';
import { useProject } from '../../../contexts/ProjectContext';
import {
  contentTemplatesApi,
  useContentTemplates,
  useCreateContentTemplate,
  useUpdateContentTemplate,
  type ContentTemplate,
  type UpdateContentTemplatePayload,
} from '../../../api/content-templates';
import { ALL_CONTENT_TYPES, CONTENT_TYPE_LABELS, type ContentType } from '../../../api/content';
import { documentsApi } from '../../../api/documents';
import { DashboardPageLayout } from '../../../components/layout';
import { Card, EmptyState } from '../../../components/ui';

// ── Variable extraction helper ─────────────────────────────────────────────

function countVariablesInContent(content: string): number {
  const seen = new Set<string>();
  const regex = /\{\{\s*(\w+)\s*(?:\|\|\s*[^}]+?\s*)?\}\}/g;
  let match: RegExpExecArray | null;

  const scan = (text: string) => {
    regex.lastIndex = 0;
    while ((match = regex.exec(text)) !== null) {
      seen.add(match[1]);
    }
  };

  if (content.startsWith('[{') || content.startsWith('[')) {
    try {
      const blocks = JSON.parse(content);
      const walk = (obj: unknown): void => {
        if (typeof obj === 'string') scan(obj);
        else if (Array.isArray(obj)) obj.forEach(walk);
        else if (obj && typeof obj === 'object') Object.values(obj).forEach(walk);
      };
      walk(blocks);
    } catch {
      scan(content);
    }
  } else {
    scan(content);
  }
  return seen.size;
}

interface ExtractedVar {
  key: string;
  label: string;
}

function extractVariables(content: string): ExtractedVar[] {
  const seen = new Map<string, ExtractedVar>();
  const regex = /\{\{\s*(\w+)\s*(?:\|\|\s*([^}]+?)\s*)?\}\}/g;
  let match: RegExpExecArray | null;

  const variableToLabel = (name: string): string =>
    name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

  const scan = (text: string) => {
    regex.lastIndex = 0;
    while ((match = regex.exec(text)) !== null) {
      const key = match[1];
      const explicitLabel = match[2]?.trim();
      if (!seen.has(key)) {
        seen.set(key, { key, label: explicitLabel || variableToLabel(key) });
      } else if (explicitLabel && !seen.get(key)!.label.includes(' ')) {
        seen.get(key)!.label = explicitLabel;
      }
    }
  };

  if (content.startsWith('[{') || content.startsWith('[')) {
    try {
      const blocks = JSON.parse(content);
      const walk = (obj: unknown): void => {
        if (typeof obj === 'string') scan(obj);
        else if (Array.isArray(obj)) obj.forEach(walk);
        else if (obj && typeof obj === 'object') Object.values(obj).forEach(walk);
      };
      walk(blocks);
    } catch {
      scan(content);
    }
  } else {
    scan(content);
  }
  return Array.from(seen.values());
}

// ============================================================================
// Template List Panel
// ============================================================================

const TYPE_ICONS: Record<string, string> = {};

function TemplateListPanel({
  templates,
  selected,
  onSelect,
  onNew,
  onDuplicate,
  onDelete,
  loading,
  variableCounts,
}: {
  templates: ContentTemplate[];
  selected: ContentTemplate | null;
  onSelect: (t: ContentTemplate) => void;
  onNew: () => void;
  onDuplicate: (t: ContentTemplate) => void;
  onDelete: (t: ContentTemplate) => void;
  loading: boolean;
  variableCounts: Map<string, number>;
}) {
  const [search, setSearch] = useState('');

  const filtered = templates.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.description || '').toLowerCase().includes(search.toLowerCase())
  );

  // Group by content_type
  const grouped = filtered.reduce<Record<string, ContentTemplate[]>>((acc, t) => {
    const key = t.content_type || 'universal';
    acc[key] = acc[key] || [];
    acc[key].push(t);
    return acc;
  }, {});

  return (
    <div className="w-[280px] flex-shrink-0 border-r border-neutral-100 bg-white flex flex-col h-full">
      <div className="px-4 py-3 border-b border-neutral-100">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-semibold text-neutral-700">Templates</h2>
          <button
            onClick={onNew}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-coral-500 text-white rounded-lg hover:bg-coral-600 transition-colors"
          >
            <Plus size={12} />
            New
          </button>
        </div>
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-300" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search templates…"
            className="w-full pl-7 pr-3 py-1.5 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-400/40 focus:border-coral-400"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={16} className="animate-spin text-neutral-300" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <LayoutTemplate size={24} className="text-neutral-300" />
            </div>
            <p className="text-sm font-medium text-neutral-600 mb-1">Create your first template</p>
            <p className="text-[11px] text-neutral-400 leading-relaxed mb-4 max-w-[200px] mx-auto">
              Templates define reusable document structures with variable placeholders for quick content generation.
            </p>
            <button
              onClick={onNew}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                bg-coral-500 text-white rounded-lg hover:bg-coral-600 transition-colors"
            >
              <Plus size={12} /> New Template
            </button>
          </div>
        ) : (
          Object.entries(grouped).map(([type, items]) => (
            <div key={type}>
              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider">
                  {type === 'universal' ? 'Universal' : CONTENT_TYPE_LABELS[type as ContentType] || type}
                </span>
                <span className="text-[10px] text-neutral-400">{items.length}</span>
              </div>
              {items.map(t => (
                <button
                  key={t.id}
                  onClick={() => onSelect(t)}
                  className={`group w-full flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50 transition-colors text-left ${
                    selected?.id === t.id ? 'bg-coral-50 border-r-2 border-coral-400' : ''
                  }`}
                >
                  <div className="w-8 h-8 rounded border border-neutral-200 bg-neutral-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {t.figma_thumbnail_url ? (
                      <img src={t.figma_thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Image size={12} className="text-neutral-300" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-neutral-800 truncate">{t.name}</span>
                      {variableCounts.has(t.id) && (variableCounts.get(t.id)! > 0) && (
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-coral-100 text-[9px] font-bold text-coral-600 flex-shrink-0"
                          title={`${variableCounts.get(t.id)} variable${variableCounts.get(t.id)! !== 1 ? 's' : ''}`}
                        >
                          {variableCounts.get(t.id)}
                        </span>
                      )}
                    </div>
                    {t.description && (
                      <div className="text-[10px] text-neutral-400 truncate">{t.description}</div>
                    )}
                  </div>
                  {/* Action buttons — visible on hover */}
                  <div className="flex-shrink-0 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); onDuplicate(t); }}
                      title="Duplicate"
                      className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600"
                    >
                      <Copy size={11} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete(t); }}
                      title="Delete"
                      className="p-1 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Template Editor Panel
// ============================================================================

function TemplateEditorPanel({
  template,
  workspaceId,
  onSaved,
  onVariablesLoaded,
}: {
  template: ContentTemplate | null;
  workspaceId: string;
  onSaved: (t: ContentTemplate) => void;
  onVariablesLoaded?: (templateId: string, count: number) => void;
}) {
  const updateMutation = useUpdateContentTemplate(workspaceId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [contentType, setContentType] = useState<ContentType | '' >('');
  const [figmaUrl, setFigmaUrl] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  // Variables extracted from linked document
  const [docVariables, setDocVariables] = useState<ExtractedVar[]>([]);
  const [varsLoading, setVarsLoading] = useState(false);

  useEffect(() => {
    if (!template) return;
    setName(template.name);
    setDescription(template.description || '');
    setContentType((template.content_type as ContentType) || '');
    setFigmaUrl(template.figma_url || '');
    setIsDefault(template.is_default);
    setIsActive(template.is_active);
  }, [template?.id]);

  // Fetch document content and extract variables when template changes
  useEffect(() => {
    if (!template?.document_id) {
      setDocVariables([]);
      return;
    }
    setVarsLoading(true);
    // document_id exists — fetch the doc to extract variables
    // We need a project_id; templates don't store it, so we try with empty string
    // The documents API may work with just the docId if it's globally unique
    documentsApi.get(template.document_id, '')
      .then((doc) => {
        const vars = extractVariables(doc.content || '');
        setDocVariables(vars);
        if (onVariablesLoaded) onVariablesLoaded(template.id, vars.length);
      })
      .catch(() => {
        setDocVariables([]);
      })
      .finally(() => setVarsLoading(false));
  }, [template?.id, template?.document_id]);

  if (!template) {
    return (
      <div className="flex-1 flex items-center justify-center bg-neutral-50">
        <EmptyState
          size="md"
          icon={<LayoutTemplate size={32} className="text-neutral-200" />}
          title="No template selected"
          description="Select a template from the list or create a new one."
        />
      </div>
    );
  }

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload: UpdateContentTemplatePayload = {
        name: name.trim(),
        description: description.trim() || null,
        content_type: (contentType as ContentType) || null,
        figma_url: figmaUrl.trim() || undefined,
        is_default: isDefault,
        is_active: isActive,
      };
      const updated = await contentTemplatesApi.update(workspaceId, template.id, payload);
      onSaved(updated);
    } catch (err) {
      console.error('Failed to save template', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-white">
      <div className="max-w-lg mx-auto px-6 py-6 space-y-5">
        <div>
          <h2 className="text-sm font-semibold text-neutral-900 mb-1">Template Settings</h2>
          <p className="text-xs text-neutral-400">Configure the template and link a Figma design. Martha can analyze the design and map slot fields via her chat panel.</p>
        </div>

        {/* Name */}
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Archie Proposal"
            className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-400/30 focus:border-coral-400"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={2}
            placeholder="What is this template for?"
            className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-400/30 focus:border-coral-400 resize-none"
          />
        </div>

        {/* Content Type */}
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Content type</label>
          <select
            value={contentType}
            onChange={e => setContentType(e.target.value as ContentType | '')}
            className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-coral-400/30 focus:border-coral-400"
          >
            <option value="">Universal (all types)</option>
            {ALL_CONTENT_TYPES.map(t => (
              <option key={t} value={t}>{CONTENT_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>

        {/* Figma URL */}
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">
            Figma URL
            <span className="ml-1 text-neutral-400 font-normal">(optional)</span>
          </label>
          <input
            type="url"
            value={figmaUrl}
            onChange={e => setFigmaUrl(e.target.value)}
            placeholder="https://www.figma.com/design/..."
            className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-coral-400/30 focus:border-coral-400"
          />
          {figmaUrl && (
            <a
              href={figmaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600 mt-1 transition-colors"
            >
              <ExternalLink size={11} />
              View in Figma
            </a>
          )}
          {template.figma_thumbnail_url && (
            <div className="mt-2">
              <img
                src={template.figma_thumbnail_url}
                alt="Figma preview"
                className="w-full max-h-48 object-contain rounded-lg border border-neutral-200 bg-neutral-50"
              />
            </div>
          )}
          {template.figma_slots && template.figma_slots.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-neutral-600 mb-1">Figma slots ({template.figma_slots.length})</p>
              <div className="space-y-1">
                {template.figma_slots.map((slot, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-neutral-500 bg-neutral-50 rounded px-2 py-1">
                    <span className="font-mono text-neutral-700">{slot.slot_name}</span>
                    <span className="text-neutral-300">·</span>
                    <span className="truncate">{slot.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-neutral-400 mt-1">
            Paste a Figma frame URL. Martha can analyze the design via her chat panel to populate slot mappings.
          </p>
        </div>

        {/* Flags */}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={e => setIsDefault(e.target.checked)}
              className="rounded border-neutral-300 text-coral-500 focus:ring-coral-400/30"
            />
            <span className="text-xs text-neutral-600">Default for type</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
              className="rounded border-neutral-300 text-coral-500 focus:ring-coral-400/30"
            />
            <span className="text-xs text-neutral-600">Active</span>
          </label>
        </div>

        {/* Extracted Variables */}
        {template.document_id && (
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1.5 flex items-center gap-1.5">
              <Braces size={12} className="text-neutral-400" />
              Document Variables
              {varsLoading && <Loader2 size={11} className="animate-spin text-neutral-300" />}
            </label>
            {docVariables.length === 0 && !varsLoading ? (
              <p className="text-[11px] text-neutral-400">
                No <code className="px-1 py-0.5 bg-neutral-100 rounded text-[10px] font-mono">{'{{VARIABLE}}'}</code> placeholders
                found in the linked document.
              </p>
            ) : (
              <div className="space-y-1">
                {docVariables.map((v) => (
                  <div
                    key={v.key}
                    className="flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-lg
                      bg-neutral-50 border border-neutral-200/60"
                  >
                    <code className="text-[10px] font-mono text-coral-600 bg-coral-50 px-1.5 py-0.5 rounded">
                      {v.key}
                    </code>
                    <span className="text-neutral-500 truncate">{v.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={saving || !name.trim()}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-coral-500 text-white rounded-lg hover:bg-coral-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Guidance Banner
// ============================================================================

function TemplateGuidanceInfo() {
  return (
    <div className="mx-4 mt-3 mb-1 px-4 py-2.5 bg-neutral-50 border border-neutral-200/60 rounded-xl">
      <div className="flex items-start gap-2.5">
        <Info size={14} className="text-neutral-400 flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-neutral-500 leading-relaxed">
          <span className="font-medium text-neutral-600">Templates</span> use{' '}
          <code className="px-1 py-0.5 bg-neutral-100 rounded text-[10px] font-mono">{'{{VARIABLE}}'}</code>{' '}
          placeholders that become form fields when generating content. Use{' '}
          <code className="px-1 py-0.5 bg-neutral-100 rounded text-[10px] font-mono">{'{{KEY || Label}}'}</code>{' '}
          for friendly labels.
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================

const ContentTemplatesPage = () => {
  const { currentProject } = useProject();
  const workspaceId = currentProject?.workspaceId || '';

  const { data: templates = [], isLoading, refetch } = useContentTemplates(workspaceId);
  const createMutation = useCreateContentTemplate(workspaceId);

  const [selected, setSelected] = useState<ContentTemplate | null>(null);
  const [variableCounts, setVariableCounts] = useState<Map<string, number>>(new Map());

  const handleVariablesLoaded = (templateId: string, count: number) => {
    setVariableCounts(prev => {
      const next = new Map(prev);
      next.set(templateId, count);
      return next;
    });
  };

  // Fetch variable counts for all templates with document_id on mount
  useEffect(() => {
    if (!templates.length) return;
    const templatesWithDocs = templates.filter(t => t.document_id);
    templatesWithDocs.forEach(t => {
      if (variableCounts.has(t.id)) return; // already fetched
      documentsApi.get(t.document_id!, '')
        .then((doc) => {
          const count = countVariablesInContent(doc.content || '');
          setVariableCounts(prev => {
            const next = new Map(prev);
            next.set(t.id, count);
            return next;
          });
        })
        .catch(() => {
          // Ignore — no count shown for this template
        });
    });
  }, [templates]);

  const handleNew = async () => {
    try {
      const t = await createMutation.mutateAsync({ name: 'New Template', is_active: true });
      await refetch();
      setSelected(t);
    } catch (err) {
      console.error('Failed to create template', err);
    }
  };

  const handleDuplicate = async (t: ContentTemplate) => {
    try {
      const dup = await createMutation.mutateAsync({
        name: `${t.name} (copy)`,
        description: t.description || undefined,
        content_type: t.content_type || undefined,
        figma_url: t.figma_url || undefined,
        is_active: true,
      });
      await refetch();
      setSelected(dup);
    } catch (err) {
      console.error('Failed to duplicate template', err);
    }
  };

  const handleDelete = async (t: ContentTemplate) => {
    if (!confirm(`Delete "${t.name}"? This can be undone by reactivating.`)) return;
    try {
      await contentTemplatesApi.delete(workspaceId, t.id);
      if (selected?.id === t.id) setSelected(null);
      await refetch();
    } catch (err) {
      console.error('Failed to delete template', err);
    }
  };

  const handleSaved = (updated: ContentTemplate) => {
    setSelected(updated);
    refetch();
  };

  return (
    <DashboardPageLayout
      title="Content Templates"
      subtitle="Manage document templates with {{VARIABLE}} placeholders and Figma design templates"
      icon={<LayoutTemplate size={20} />}
    >
      <TemplateGuidanceInfo />
      <div className="flex h-[calc(100vh-8rem)] -mx-6 -mt-4 overflow-hidden rounded-xl border border-neutral-100">
        <TemplateListPanel
          templates={templates}
          selected={selected}
          onSelect={setSelected}
          onNew={handleNew}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          loading={isLoading || createMutation.isPending}
          variableCounts={variableCounts}
        />
        <TemplateEditorPanel
          template={selected}
          workspaceId={workspaceId}
          onSaved={handleSaved}
          onVariablesLoaded={handleVariablesLoaded}
        />
      </div>
    </DashboardPageLayout>
  );
};

export default ContentTemplatesPage;
